import { supabase } from "@/integrations/supabase/client";
import { UserProgress } from "./gamification";
import { trackEvent } from "./analytics";
import type { BadgeEvent } from "./badgeEvents";
import { BADGE_EVENT_MAP } from "./badgeEvents";

export type BadgePillar = 'starting' | 'awareness' | 'consistency' | 'recovery' | 'depth';

export interface Badge {
  id: string;
  name: string;
  description: string;
  iconName: string;
  pillar: BadgePillar;
  xpAward: number;
  shareable: boolean;
  checkCriteria: (userId: string, progress: UserProgress, context?: Record<string, unknown>) => Promise<boolean>;
}

export const BADGES: Record<string, Badge> = {
  // ── Pillar 1: Starting ──
  FIRST_EXPENSE: {
    id: 'first_expense',
    name: 'First Step',
    description: 'Logged your first expense!',
    iconName: 'Footprints',
    pillar: 'starting',
    xpAward: 50,
    shareable: false,
    checkCriteria: async (_userId, progress) => progress.total_expenses_logged >= 1,
  },
  NAMED: {
    id: 'named',
    name: "You're You",
    description: 'Set a name in profile',
    iconName: 'Smile',
    pillar: 'starting',
    xpAward: 30,
    shareable: false,
    checkCriteria: async (userId) => {
      const { data } = await supabase
        .from('budget_settings')
        .select('language')
        .eq('user_id', userId)
        .maybeSingle();
      // We check profile name via a different mechanism — 
      // this badge is awarded event-trigger only from ProfileSettings
      return false; // event-triggered only
    },
  },
  FOUND_YOUR_STYLE: {
    id: 'found_your_style',
    name: 'Your Kind of App',
    description: 'Changed motivation style from default',
    iconName: 'Sliders',
    pillar: 'starting',
    xpAward: 30,
    shareable: false,
    checkCriteria: async (userId) => {
      const { data } = await supabase
        .from('budget_settings')
        .select('motivation_style')
        .eq('user_id', userId)
        .maybeSingle();
      return !!data && data.motivation_style !== 'gentle';
    },
  },

  // ── Pillar 2: Consistency ──
  SEVEN_DAYS: {
    id: 'seven_days',
    name: 'One Week',
    description: '7-day streak',
    iconName: 'Calendar',
    pillar: 'consistency',
    xpAward: 100,
    shareable: true,
    checkCriteria: async (_userId, progress) =>
      progress.current_streak >= 7 || progress.longest_streak >= 7,
  },
  THIRTY_DAYS: {
    id: 'thirty_days',
    name: 'One Month',
    description: '30-day streak',
    iconName: 'Sun',
    pillar: 'consistency',
    xpAward: 250,
    shareable: true,
    checkCriteria: async (_userId, progress) =>
      progress.current_streak >= 30 || progress.longest_streak >= 30,
  },
  NINETY_DAYS: {
    id: 'ninety_days',
    name: 'Three Months',
    description: '90-day streak',
    iconName: 'Star',
    pillar: 'consistency',
    xpAward: 500,
    shareable: true,
    checkCriteria: async (_userId, progress) =>
      progress.current_streak >= 90 || progress.longest_streak >= 90,
  },
  LONGEST_STREAK: {
    id: 'longest_streak',
    name: 'Personal Best',
    description: 'Beat your previous longest streak',
    iconName: 'Trophy',
    pillar: 'consistency',
    xpAward: 150,
    shareable: true,
    checkCriteria: async (_userId, progress, context) => {
      const prevLongest = (context?.previousLongest as number) ?? 0;
      return progress.current_streak > prevLongest && progress.current_streak >= 3;
    },
  },

  // ── Pillar 3: Recovery ⭐ ──
  STILL_HERE: {
    id: 'still_here',
    name: 'Still Here',
    description: 'Logged after a 7+ day gap',
    iconName: 'Heart',
    pillar: 'recovery',
    xpAward: 75,
    shareable: true,
    checkCriteria: async (_userId, _progress, context) => {
      return (context?.daysSinceLast as number) >= 7;
    },
  },
  AGAIN: {
    id: 'again',
    name: 'Again',
    description: 'Complete a 7-day streak after previously breaking one',
    iconName: 'RefreshCw',
    pillar: 'recovery',
    xpAward: 100,
    shareable: true,
    checkCriteria: async (_userId, progress) => {
      return progress.current_streak >= 7 && (progress.streaks_broken_count ?? 0) >= 1;
    },
  },

  // ── Pillar 4: Awareness ──
  THE_LONG_VIEW: {
    id: 'the_long_view',
    name: 'The Long View',
    description: 'Opened spending summary 4 weeks in a row',
    iconName: 'Eye',
    pillar: 'awareness',
    xpAward: 80,
    shareable: false,
    checkCriteria: async (userId) => {
      const sixWeeksAgo = new Date();
      sixWeeksAgo.setDate(sixWeeksAgo.getDate() - 42);
      const { data } = await supabase
        .from('user_engagement_events')
        .select('event_date')
        .eq('user_id', userId)
        .eq('event_type', 'summary_viewed')
        .gte('event_date', sixWeeksAgo.toISOString().split('T')[0]);
      if (!data || data.length < 4) return false;
      const weeks = new Set(data.map(d => {
        const date = new Date(d.event_date);
        const yearStart = new Date(date.getFullYear(), 0, 1);
        const weekNum = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + yearStart.getDay() + 1) / 7);
        return `${date.getFullYear()}-W${weekNum}`;
      }));
      return weeks.size >= 4;
    },
  },
  PATTERN_SPOTTER: {
    id: 'pattern_spotter',
    name: 'Pattern Spotter',
    description: 'Same merchant logged 5+ times',
    iconName: 'TrendingUp',
    pillar: 'awareness',
    xpAward: 60,
    shareable: false,
    checkCriteria: async (_userId, progress) => {
      return (progress.max_merchant_count ?? 0) >= 5;
    },
  },
  SUBSCRIPTION_HUNTER: {
    id: 'subscription_hunter',
    name: 'Subscription Hunter',
    description: 'Tag 3 expenses as recurring',
    iconName: 'Repeat',
    pillar: 'awareness',
    xpAward: 80,
    shareable: false,
    checkCriteria: async (_userId, progress) => {
      return (progress.recurring_count ?? 0) >= 3;
    },
  },
  BUDGET_FINISHER: {
    id: 'budget_finisher',
    name: 'Budget Finisher',
    description: 'Stay under budget for a full calendar month',
    iconName: 'CheckCircle',
    pillar: 'awareness',
    xpAward: 120,
    shareable: true,
    checkCriteria: async (userId, progress) => {
      const monthsUnderBudget = (progress.months_under_budget as string[]) ?? [];
      return monthsUnderBudget.length >= 1;
    },
  },

  // ── Pillar 5: Depth ──
  COFFEE_LOVER: {
    id: 'coffee_lover',
    name: 'Coffee Enthusiast',
    description: '10 expenses with "coffee" in merchant or note',
    iconName: 'Coffee',
    pillar: 'depth',
    xpAward: 60,
    shareable: true,
    checkCriteria: async (_userId, progress) => {
      return (progress.coffee_count ?? 0) >= 10;
    },
  },
  TEMPLATE_MAKER: {
    id: 'template_maker',
    name: 'Template Maker',
    description: 'Create your first expense template',
    iconName: 'Bookmark',
    pillar: 'depth',
    xpAward: 50,
    shareable: false,
    checkCriteria: async (userId) => {
      const { count } = await supabase
        .from('expense_templates')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      return (count ?? 0) >= 1;
    },
  },
  TRAVELLER: {
    id: 'traveller',
    name: 'On The Move',
    description: 'Log an expense in a different currency than home',
    iconName: 'Globe',
    pillar: 'depth',
    xpAward: 80,
    shareable: true,
    checkCriteria: async (_userId, _progress, context) => {
      return (context?.foreignCurrency as boolean) === true;
    },
  },
  CALM_ACHIEVER: {
    id: 'calm_achiever',
    name: 'Quiet Achiever',
    description: 'Use Calm Mode for 14 consecutive days',
    iconName: 'Moon',
    pillar: 'depth',
    xpAward: 100,
    shareable: false,
    checkCriteria: async (userId) => {
      const { data } = await supabase
        .from('budget_settings')
        .select('hide_rewards, calm_mode_enabled_since')
        .eq('user_id', userId)
        .maybeSingle();
      if (!data?.hide_rewards || !data.calm_mode_enabled_since) return false;
      const enabledSince = new Date(data.calm_mode_enabled_since);
      const daysSince = Math.floor((Date.now() - enabledSince.getTime()) / (1000 * 60 * 60 * 24));
      return daysSince >= 14;
    },
  },
  XP_COLLECTOR: {
    id: 'xp_collector',
    name: 'XP Collector',
    description: 'Accumulate 5,000 XP',
    iconName: 'Zap',
    pillar: 'depth',
    xpAward: 200,
    shareable: false,
    checkCriteria: async (_userId, progress) => progress.xp >= 5000,
  },
};

// Pillar metadata for UI
export const PILLAR_META: Record<BadgePillar, { label: string; color: string; textColor: string }> = {
  starting:    { label: 'Starting',    color: 'bg-blue-50 dark:bg-blue-950/30',   textColor: 'text-blue-700 dark:text-blue-300' },
  awareness:   { label: 'Awareness',   color: 'bg-purple-50 dark:bg-purple-950/30', textColor: 'text-purple-700 dark:text-purple-300' },
  consistency: { label: 'Consistency', color: 'bg-amber-50 dark:bg-amber-950/30',  textColor: 'text-amber-700 dark:text-amber-300' },
  recovery:    { label: 'Recovery',    color: 'bg-green-50 dark:bg-green-950/30',  textColor: 'text-green-700 dark:text-green-300' },
  depth:       { label: 'Depth',       color: 'bg-slate-50 dark:bg-slate-950/30',  textColor: 'text-slate-600 dark:text-slate-400' },
};

// Centralized helper to get badge by ID with fallback formatting
export const getBadgeById = (badgeId: string): Badge => {
  const badge = Object.values(BADGES).find(b => b.id === badgeId);
  if (badge) return badge;

  const formattedName = badgeId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return {
    id: badgeId,
    name: formattedName,
    iconName: 'Award',
    description: '',
    pillar: 'depth',
    xpAward: 0,
    shareable: false,
    checkCriteria: async () => false,
  };
};

/**
 * Event-driven badge evaluation. Only checks badges relevant to the event.
 */
export const checkAndAwardBadgesForEvent = async (
  userId: string,
  event: BadgeEvent,
  progress: UserProgress,
  context?: Record<string, unknown>
): Promise<string[]> => {
  const relevantBadgeIds = BADGE_EVENT_MAP[event] ?? [];
  if (relevantBadgeIds.length === 0) return [];

  const newBadges: string[] = [];

  // Get existing badges
  const { data: existingBadges } = await supabase
    .from('user_badges')
    .select('badge_id')
    .eq('user_id', userId);

  const earnedBadgeIds = new Set(existingBadges?.map(b => b.badge_id) ?? []);

  for (const badge of Object.values(BADGES)) {
    if (!relevantBadgeIds.includes(badge.id)) continue;
    if (earnedBadgeIds.has(badge.id)) continue;

    try {
      const earned = await badge.checkCriteria(userId, progress, context);
      if (earned) {
        const { error } = await supabase
          .from('user_badges')
          .insert({ user_id: userId, badge_id: badge.id, seen: false });
        if (!error) {
          newBadges.push(badge.id);
          trackEvent({ name: 'badge_earned', props: { badge_id: badge.id } });

          // Award XP for badge
          if (badge.xpAward > 0) {
            await supabase
              .from('user_progress')
              .update({ xp: progress.xp + badge.xpAward })
              .eq('user_id', userId);
            progress.xp += badge.xpAward;
          }
        }
      }
    } catch (error) {
      console.error(`Error checking badge ${badge.id}:`, error);
    }
  }

  return newBadges;
};

/** Legacy compat — checks ALL badges (used on Badges page retroactive scan) */
export const checkAndAwardBadges = async (
  userId: string,
  progress: UserProgress
): Promise<string[]> => {
  const newBadges: string[] = [];
  const { data: existingBadges } = await supabase
    .from('user_badges')
    .select('badge_id')
    .eq('user_id', userId);
  const earnedBadgeIds = new Set(existingBadges?.map(b => b.badge_id) ?? []);

  for (const badge of Object.values(BADGES)) {
    if (earnedBadgeIds.has(badge.id)) continue;
    try {
      const earned = await badge.checkCriteria(userId, progress);
      if (earned) {
        const { error } = await supabase
          .from('user_badges')
          .insert({ user_id: userId, badge_id: badge.id, seen: false });
        if (!error) {
          newBadges.push(badge.id);
          trackEvent({ name: 'badge_earned', props: { badge_id: badge.id } });
        }
      }
    } catch (error) {
      console.error(`Error checking badge ${badge.id}:`, error);
    }
  }
  return newBadges;
};

export const getUserBadges = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_badges')
    .select('*')
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });
  if (error) { console.error('Error fetching badges:', error); return []; }
  return data;
};

export const markBadgesAsSeen = async (userId: string, badgeIds: string[]) => {
  const { error } = await supabase
    .from('user_badges')
    .update({ seen: true })
    .eq('user_id', userId)
    .in('badge_id', badgeIds);
  if (error) console.error('Error marking badges as seen:', error);
};

export const retroactivelyAwardBadges = async (userId: string) => {
  const { data: progress } = await supabase
    .from('user_progress')
    .select('*')
    .eq('user_id', userId)
    .single();
  if (!progress) { console.error('No progress found for user'); return []; }
  return await checkAndAwardBadges(userId, {
    ...progress,
    streak_state: (progress.streak_state as import('./gamification').StreakState) ?? 'active',
    streaks_broken_count: progress.streaks_broken_count ?? 0,
    coffee_count: progress.coffee_count ?? 0,
    max_merchant_count: progress.max_merchant_count ?? 0,
    recurring_count: progress.recurring_count ?? 0,
    months_under_budget: (progress.months_under_budget as string[]) ?? [],
  });
};
